import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, shareReplay, take, timer } from 'rxjs';
import { ConfigService } from '@app/services';
import { distinctUntilChanged, map, skip, switchMap } from 'rxjs/operators';
import { InteractionService } from '@core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type TintingMode = 'low' | 'medium' | 'high';

@Injectable({
  providedIn: 'root',
})
export class BrightnessService {

  private readonly destroyRef = inject(DestroyRef);
  private readonly configService = inject(ConfigService);
  private readonly interactionService = inject(InteractionService);

  private readonly tintingSubject = new BehaviorSubject<number>(0);

  public readonly config$ = this.configService.config$.pipe(
    map(({ brightness }) => brightness),
    shareReplay(1),
  );

  public readonly tinting$ = this.tintingSubject.asObservable();

  public readonly tintingModes: Record<TintingMode, number> = {
    high: 0,
    medium: 44,
    low: 60,
  };

  public initialize(): void {
    this.interactionService.idle$.pipe(
      distinctUntilChanged(),
      skip(1),
      switchMap((idle) => {
        if (!idle) {
          return EMPTY;
        }

        return this.config$.pipe(
          take(1),
          switchMap(({ resetDelayAfterIdleDelay = 0 }) => {
            return timer(resetDelayAfterIdleDelay * 1000);
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.setTinting(0);
    });
  }

  public setTinting(value: number) {
    this.tintingSubject.next(value);
  }

  public getTinting(): number {
    return this.tintingSubject.getValue();
  }

  public setTintingMode(mode: TintingMode) {
    const value = this.tintingModes[mode] || 0;
    this.setTinting(value);
  }

  public getTintingMode(): TintingMode {
    const value = this.getTinting();

    if (value >= this.tintingModes.low) {
      return 'low';
    }
    else if (value >= this.tintingModes.medium) {
      return 'medium';
    }
    else {
      return 'high';
    }
  }

}
